import './markdown-editor.scss';
import {ReactNode, useState, useRef} from 'react';
import {
  Editor,
  EditorState,
  ContentState,
  RichUtils,
  DraftEditorCommand,
  ContentBlock,
  convertToRaw,
  convertFromRaw,
  RawDraftContentState,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Paper from '@mui/material/Paper';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import Typography from '@mui/material/Typography';
import CodeIcon from '@mui/icons-material/Code';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import {red} from '@mui/material/colors';
import {draftjsToMd, mdToDraftjs} from 'draftjs-md-converter';
import {Tooltip} from '../tooltip/tooltip';

// Create Logger
// import { Logger } from '../utils/logger';
// const logger = new Logger('MarkdownEditor');

// Example
// https://codepen.io/Kiwka/embed/YNYvyG?height=265&theme-id=0&slug-hash=YNYvyG&default-tab=js%2Cresult&user=Kiwka&embed-version=2&pen-title=Rich%20Text%20-%20Draft.js%20example

// Custom overrides for "code" style.
const styleMap = {
  CODE: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
  },
};

function getBlockStyle(block: ContentBlock): any {
  switch (block.getType()) {
    case 'blockquote':
      return 'RichEditor-blockquote';
    default:
      return null;
  }
}

const StyleButton = (props: {
  readOnly?: boolean;
  onToggle: (e: any) => void;
  label: string;
  active?: boolean;
  style: string;
}) => {
  // Methods
  const onToggle = (e: any) => {
    // Prevent default action
    e.preventDefault();

    // Toggle style
    props.onToggle(props.style);
  };

  return (
    <ToggleButton
      disabled={props.readOnly}
      size="small"
      value={props.label}
      onMouseDown={onToggle}
      selected={props.active}
      sx={{width: '36px', height: '36px'}}
    >
      {/** Inline */}
      {props.label === 'Bold' ? <FormatBoldIcon fontSize="small" /> : undefined}
      {props.label === 'Italic' ? <FormatItalicIcon fontSize="small" /> : undefined}
      {props.label === 'Underline' ? <FormatUnderlinedIcon fontSize="small" /> : undefined}
      {props.label === 'Monospace' ? <CodeIcon fontSize="small" /> : undefined}

      {/** Block */}
      {props.label === 'H1' ? (
        <Typography fontWeight="bold" variant="body2">
          H1
        </Typography>
      ) : undefined}
      {props.label === 'H2' ? (
        <Typography fontWeight="bold" variant="body2">
          H2
        </Typography>
      ) : undefined}
      {props.label === 'H3' ? (
        <Typography fontWeight="bold" variant="body2">
          H3
        </Typography>
      ) : undefined}
      {props.label === 'H4' ? (
        <Typography fontWeight="bold" variant="body2">
          H4
        </Typography>
      ) : undefined}
      {props.label === 'H5' ? (
        <Typography fontWeight="bold" variant="body2">
          H5
        </Typography>
      ) : undefined}
      {props.label === 'H6' ? (
        <Typography fontWeight="bold" variant="body2">
          H6
        </Typography>
      ) : undefined}
      {props.label === 'Blockquote' ? <FormatQuoteIcon fontSize="small" /> : undefined}
      {props.label === 'UL' ? <FormatListBulletedIcon fontSize="small" /> : undefined}
      {props.label === 'OL' ? <FormatListNumberedIcon fontSize="small" /> : undefined}
      {props.label === 'Code Block' ? <DeveloperModeIcon fontSize="small" /> : undefined}
    </ToggleButton>
  );
};

var INLINE_STYLES = [
  {label: 'Bold', style: 'BOLD', key: 'bold'},
  {label: 'Italic', style: 'ITALIC', key: 'italic'},
  {label: 'Underline', style: 'UNDERLINE', key: 'underline'},
  {label: 'Monospace', style: 'CODE', key: 'monospace'},
];
const InlineStyleControls = (props: {
  configuration?: InlineStyleControlsMarkdownEditorConfiguration;
  editorState: EditorState;
  readOnly?: boolean;
  onToggle: (e: any) => void;
}) => {
  // Get Current inline style
  var currentStyle = props.editorState.getCurrentInlineStyle();

  return (
    <ToggleButtonGroup
      size="small"
      className="RichEditor-controls"
      sx={{display: 'flex', flexWrap: 'wrap'}}
    >
      {INLINE_STYLES.map((type) => {
        if (props.configuration && (props.configuration as any)[type.key]) {
          return (
            <StyleButton
              readOnly={props.readOnly}
              key={type.label}
              active={currentStyle.has(type.style)}
              label={type.label}
              onToggle={props.onToggle}
              style={type.style}
            />
          );
        }
        return undefined;
      })}
    </ToggleButtonGroup>
  );
};

const BLOCK_TYPES = [
  {label: 'H1', style: 'header-one', key: 'h1'},
  {label: 'H2', style: 'header-two', key: 'h2'},
  {label: 'H3', style: 'header-three', key: 'h3'},
  {label: 'H4', style: 'header-four', key: 'h4'},
  {label: 'H5', style: 'header-five', key: 'h5'},
  {label: 'H6', style: 'header-six', key: 'h6'},
  {label: 'Blockquote', style: 'blockquote', key: 'blockquote'},
  {label: 'UL', style: 'unordered-list-item', key: 'ul'},
  {label: 'OL', style: 'ordered-list-item', key: 'ol'},
  {label: 'Code Block', style: 'code-block', key: 'codeBlock'},
];
const BlockStyleControls = (props: {
  configuration?: BlockStyleControlsMarkdownEditorConfiguration;
  editorState: EditorState;
  readOnly?: boolean;
  onToggle: (e: any) => void;
}) => {
  // Expose props
  const {editorState} = props;

  // Get current selection
  const selection = editorState.getSelection();

  // Define block type
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  return (
    <ToggleButtonGroup
      size="small"
      className="RichEditor-controls"
      sx={{display: 'flex', flexWrap: 'wrap'}}
    >
      {BLOCK_TYPES.map((type) => {
        if (props.configuration && (props.configuration as any)[type.key]) {
          return (
            <StyleButton
              readOnly={props.readOnly}
              key={type.label}
              active={type.style === blockType}
              label={type.label}
              onToggle={props.onToggle}
              style={type.style}
            />
          );
        }
        return undefined;
      })}
    </ToggleButtonGroup>
  );
};

export interface InlineStyleControlsMarkdownEditorConfiguration {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  monospace?: boolean;
}

export interface BlockStyleControlsMarkdownEditorConfiguration {
  h1?: boolean;
  h2?: boolean;
  h3?: boolean;
  h4?: boolean;
  h5?: boolean;
  h6?: boolean;
  blockquote?: boolean;
  ul?: boolean;
  ol?: boolean;
  codeBlock?: boolean;
}

export interface MarkdownEditorConfiguration {
  inlineStyleControls?: InlineStyleControlsMarkdownEditorConfiguration;
  blockStyleControls?: BlockStyleControlsMarkdownEditorConfiguration;
}

const myMarkdownDictionaryDraftjsToMd = {
  BOLD: '**',
};

const myMarkdownDictionaryMdToDraftjs = {
  inlineStyles: {
    Strong: {
      type: 'BOLD',
      symbol: '**',
    },
  },
};

/**
 * Markdown text field.
 */
export function MarkdownEditor(props: {
  label: string;
  placeholder?: string;
  initialValue?: string;
  onChange?: (newValue: string) => void;
  onBlur?: () => void;
  minHeight?: number;
  required?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  helperText?: string;
  configuration?: MarkdownEditorConfiguration;
  disabledTooltip?: string | ReactNode;
}) {
  // Hold State
  const [editorState, setEditorState] = useState<EditorState>(() => {
    // Evaluate existence of an initialValue
    if (props.initialValue && props.initialValue.trim() !== '') {
      // Get raw data
      const rawData: RawDraftContentState = mdToDraftjs(
        props.initialValue,
        myMarkdownDictionaryMdToDraftjs
      );

      // Initialize new Content State
      const contentState: ContentState = convertFromRaw(rawData);

      // Go with initial content
      return EditorState.createWithContent(contentState);
    } else {
      // Go empty state
      return EditorState.createEmpty();
    }
  });

  // Hold ref
  const editorRef = useRef(null);

  // Private methods
  function convertStateToMarkdown(currentState: EditorState): string {
    // Convert to Raw
    const rawObject: RawDraftContentState = convertToRaw(currentState.getCurrentContent());

    // Convert to Markdown
    return draftjsToMd(rawObject, myMarkdownDictionaryDraftjsToMd);
  }

  // methods
  const focusEditor = () => {
    // If editor is available
    if (editorRef && editorRef.current) {
      // Place Focus on it
      (editorRef.current as any).focus();
    }
  };

  const dispatchValue = (currentState: EditorState) => {
    // Update Value
    if (props.onChange) {
      // Invoke it
      props.onChange(convertStateToMarkdown(currentState));
    }
  };

  const editorChanged = (newState: EditorState) => {
    // Update Editor
    setEditorState(newState);

    // Dispatch new value
    dispatchValue(newState);
  };

  const handleKeyCommand = (command: DraftEditorCommand, editorState: EditorState) => {
    // Evaluate if enabled
    if (
      !props.configuration ||
      !props.configuration.inlineStyleControls ||
      !(props.configuration.inlineStyleControls as any)[command]
    ) {
      // This key is not enabled
      return 'not-handled';
    }

    // Calculate new State
    const newState = RichUtils.handleKeyCommand(editorState, command);

    // If a new state got defined
    if (newState) {
      // Update editor state
      setEditorState(newState);

      // Set as handled
      return 'handled';
    }

    // Set as not handled
    return 'not-handled';
  };

  const toggleInlineStyle = (style: any) => {
    // Set new state
    const newState: EditorState = RichUtils.toggleInlineStyle(editorState, style);

    // Update
    setEditorState(newState);

    // Dispatch new value
    dispatchValue(newState);
  };

  const toggleBlockType = (blockType: any) => {
    // Set new state
    const newState: EditorState = RichUtils.toggleBlockType(editorState, blockType);

    // Update
    setEditorState(newState);

    // Dispatch new value
    dispatchValue(newState);
  };

  const surfaceBorderColor = props.invalid ? red[500] : '#e4e3e3';

  return (
    <FormControl
      className={`markdown-editor flat-input ${props.disabled ? 'disabled' : ''}`}
      sx={{width: '100%'}}
    >
      <Box className="markdown-editor-header">
        <Box className="label-container">
          <Typography
            component="label"
            noWrap
            sx={{color: props.invalid ? red[500] : props.disabled ? 'text.disabled' : undefined}}
          >
            {props.label}
          </Typography>
          {props.required === true && !props.disabled ? (
            <Typography component="span">*</Typography>
          ) : null}
        </Box>
        <Box className="markdown-editor-header-buttons">
          <InlineStyleControls
            readOnly={props.disabled}
            editorState={editorState}
            onToggle={toggleInlineStyle}
            configuration={props.configuration?.inlineStyleControls}
          />
          <Box className="separator"></Box>
          <BlockStyleControls
            readOnly={props.disabled}
            editorState={editorState}
            onToggle={toggleBlockType}
            configuration={props.configuration?.blockStyleControls}
          />
        </Box>
      </Box>
      <Tooltip.NotAllowedIf
        condition={props.disabled && !!props.disabledTooltip}
        title={props.disabledTooltip}
        sx={{display: 'block', width: '100%'}}
      >
        <Paper
          className="markdown-editor-surface"
          component="div"
          elevation={0}
          sx={{
            backgroundColor: '#f5f5f5',
            width: '100%',
            padding: '8.5px 12px',
            minHeight: props.minHeight ? `${props.minHeight}px` : '86px',
            fontFamily: "'Montserrat', sans-serif",
            color: props.disabled ? 'text.disabled' : undefined,
            cursor: props.disabled ? 'not-allowed' : 'text',
            border: 'none',
            borderBottom: `2px solid ${surfaceBorderColor}`,
            borderRadius: '5px 5px 0 0',
          }}
          onClick={focusEditor}
        >
          <Editor
            readOnly={props.disabled}
            blockStyleFn={getBlockStyle}
            customStyleMap={styleMap}
            editorState={editorState}
            handleKeyCommand={handleKeyCommand}
            onChange={editorChanged}
            placeholder={props.placeholder}
            ref={editorRef}
            spellCheck={true}
            stripPastedStyles={true}
            onBlur={props.onBlur}
          />
        </Paper>
      </Tooltip.NotAllowedIf>
      {props.helperText ? (
        <Typography className="markdown-editor-helper-text" variant="caption" color="error">
          {props.helperText}
        </Typography>
      ) : undefined}
    </FormControl>
  );
}
